
import { useState, type FormEvent } from 'react';
import { motion } from 'framer-motion';

const FORM_URL = 'https://readdy.ai/api/form/d6afoo7mvg9ih2c7aipg';

export default function ContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    const form = e.currentTarget;
    const formData = new FormData(form);

    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      setError('Wiadomość nie może przekraczać 500 znaków.');
      return;
    }

    setIsSubmitting(true);

    try {
      const urlEncoded = new URLSearchParams();
      formData.forEach((value, key) => {
        urlEncoded.append(key, value.toString());
      });

      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });

      if (response.ok) {
        setSubmitted(true);
        form.reset();
      } else {
        setError('Wystąpił błąd. Spróbuj ponownie.');
      }
    } catch (err) {
      console.error('Submission error:', err);
      setError('Wystąpił błąd połączenia. Spróbuj ponownie.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#111111] border border-[#00E5FF]/30 p-8 sm:p-12 text-center"
      >
        <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mx-auto mb-4 sm:mb-6 bg-[#00E5FF]/10 border border-[#00E5FF]">
          <i className="ri-check-double-line text-3xl sm:text-4xl text-[#00E5FF]"></i>
        </div>
        <h3
          className="text-xl sm:text-3xl font-bold text-white mb-3 sm:mb-4"
          style={{ fontFamily: 'Orbitron, sans-serif' }}
        >
          Wiadomość wysłana
        </h3>
        <p className="text-[#C0C7CF] text-xs sm:text-sm mb-6 sm:mb-8" style={{ fontFamily: 'Inter, sans-serif' }}>
          Dziękujemy za kontakt. Odpowiemy w ciągu 24 godzin.
        </p>
        <button
          onClick={() => setSubmitted(false)}
          className="px-6 sm:px-8 py-3 bg-[#00E5FF] text-[#050505] font-bold text-xs sm:text-sm hover:bg-white transition-all duration-300 cursor-pointer whitespace-nowrap"
          style={{ fontFamily: 'Orbitron, sans-serif' }}
        >
          Wyślij kolejną
        </button>
      </motion.div>
    );
  }

  const inputBase =
    'w-full bg-[#1A1A1A] border border-[#333333] text-white text-xs sm:text-sm px-4 sm:px-5 focus:border-[#00E5FF] focus:shadow-[0_0_15px_rgba(0,229,255,0.15)] outline-none transition-all duration-300 placeholder-[#555555]';

  return (
    <form
      id="contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="space-y-4 sm:space-y-6"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        <div>
          <label
            className="block text-[#C0C7CF] text-[10px] sm:text-xs uppercase tracking-wider mb-1.5 sm:mb-2"
            style={{ fontFamily: 'Orbitron, sans-serif' }}
          >
            Imię i Nazwisko *
          </label>
          <input
            type="text"
            name="name"
            required
            placeholder="Jan Kowalski"
            className={`${inputBase} h-12 sm:h-14`}
            style={{ fontFamily: 'Inter, sans-serif' }}
          />
        </div>
        <div>
          <label
            className="block text-[#C0C7CF] text-[10px] sm:text-xs uppercase tracking-wider mb-1.5 sm:mb-2"
            style={{ fontFamily: 'Orbitron, sans-serif' }}
          >
            Firma *
          </label>
          <input
            type="text"
            name="company"
            required
            placeholder="Nazwa firmy"
            className={`${inputBase} h-12 sm:h-14`}
            style={{ fontFamily: 'Inter, sans-serif' }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        <div>
          <label
            className="block text-[#C0C7CF] text-[10px] sm:text-xs uppercase tracking-wider mb-1.5 sm:mb-2"
            style={{ fontFamily: 'Orbitron, sans-serif' }}
          >
            Email *
          </label>
          <input
            type="email"
            name="email"
            required
            placeholder="jan@firma.pl"
            className={`${inputBase} h-12 sm:h-14`}
            style={{ fontFamily: 'Inter, sans-serif' }}
          />
        </div>
        <div>
          <label
            className="block text-[#C0C7CF] text-[10px] sm:text-xs uppercase tracking-wider mb-1.5 sm:mb-2"
            style={{ fontFamily: 'Orbitron, sans-serif' }}
          >
            Telefon
          </label>
          <input
            type="tel"
            name="phone"
            placeholder="+48 000 000 000"
            className={`${inputBase} h-12 sm:h-14`}
            style={{ fontFamily: 'Inter, sans-serif' }}
          />
        </div>
      </div>

      <div>
        <label
          className="block text-[#C0C7CF] text-[10px] sm:text-xs uppercase tracking-wider mb-1.5 sm:mb-2"
          style={{ fontFamily: 'Orbitron, sans-serif' }}
        >
          Temat
        </label>
        <select
          name="subject"
          className={`${inputBase} h-12 sm:h-14 cursor-pointer appearance-none`}
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          <option value="Zapytanie ogólne">Zapytanie ogólne</option>
          <option value="Konstrukcje stalowe">Konstrukcje stalowe</option>
          <option value="Spawalnictwo">Spawalnictwo</option>
          <option value="Obróbka CNC">Obróbka CNC</option>
          <option value="Oprzyrządowanie">Oprzyrządowanie</option>
          <option value="Wycena projektu">Wycena projektu</option>
        </select>
      </div>

      <div>
        <label
          className="block text-[#C0C7CF] text-[10px] sm:text-xs uppercase tracking-wider mb-1.5 sm:mb-2"
          style={{ fontFamily: 'Orbitron, sans-serif' }}
        >
          Wiadomość *
        </label>
        <textarea
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="Opisz swój projekt lub zapytanie..."
          className={`${inputBase} p-4 sm:p-5 resize-none`}
          style={{ fontFamily: 'Inter, sans-serif' }}
        ></textarea>
        <p className="text-[#555555] text-[10px] sm:text-xs mt-1" style={{ fontFamily: 'Inter, sans-serif' }}>
          Maks. 500 znaków
        </p>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 p-3 sm:p-4 text-red-400 text-xs sm:text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full h-14 sm:h-16 bg-[#00E5FF] text-[#050505] font-bold text-xs sm:text-sm uppercase tracking-wider hover:bg-white transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 sm:gap-3 active:scale-[0.98]"
        style={{ fontFamily: 'Orbitron, sans-serif' }}
      >
        {isSubmitting ? (
          <>
            <i className="ri-loader-4-line text-base sm:text-lg animate-spin"></i>
            Wysyłanie...
          </>
        ) : (
          <>
            Wyślij wiadomość
            <i className="ri-send-plane-line text-base sm:text-lg"></i>
          </>
        )}
      </button>
    </form>
  );
}
